import { Container, Graphics, Sprite, Text, Rectangle, Ticker, type Texture } from 'pixi.js';
import { Scene } from '../app/SceneManager';
import { COLORS, BORDER, SCRIM } from '../app/theme';
import { viewport } from '../app/viewport';
import { Panel, Button, makeText, sceneBackground, avatarSprite, iconSprite } from '../ui/widgets';
import { ChatControl } from '../ui/chatControl';
import { showRotateHint, hideRotateHint } from '../ui/rotateHint';
import { animate, prefersReducedMotion, scaleIn, pulse, type TweenHandle } from '../app/anim';
import { turnRing, glowRing, confettiBurst } from '../ui/effects';
import { initSound, playSfx, isSfxMuted, setSfxMuted } from '../app/sound';
import { loadTableTextures, type TableTextures } from '../assets/tableAssets';
import type { AppContext } from '../app/AppContext';
import { MessageTag } from '../net/messageTags';
import { Role, RoomName } from '../net/enums';
import { type GameInfoSeat, encodeExitView, encodeReadyToPlay, encodeResignGame } from '../net/codec/matchmaking';
import { decodeShowEndGameResult, decodeDisconnect } from '../net/codec/blackjack';
import { decodeChat, encodeChat } from '../net/codec/chat';
import { decodeInGameMessage } from '../net/codec/hokm';
import {
  decodeGetGame,
  decodeRollDice,
  decodeMoveEcho,
  decodeSwitchTurn,
  decodeStartGamePanel,
  encodeMove,
  encodeRollDice,
  encodeUndo,
  encodeRefresh,
  encodeGetGame,
} from '../net/codec/backgammon';
import { BackgammonLogic } from '../game/backgammon/engine';
import {
  BeadColor,
  BACKGAMMON_WAIT_ROW_WHITE,
  BACKGAMMON_WAIT_ROW_BLACK,
  BACKGAMMON_OUT_ROW_WHITE,
  BACKGAMMON_OUT_ROW_BLACK,
} from '../game/backgammon/constants';
import type { HighLightColumnDto } from '../game/backgammon/types';
import type { TimeBlock } from '../net/models/backgammonGame';
import type { BinaryReader } from '../net/BinaryReader';
import { toPersianDigits } from '../util/persian';
import {
  CHECKER_COLUMN_FILL,
  CHECKER_EDGE_INSET,
  backgammonDiceStrip,
  bearingOffTrayHitArea,
  checkerStackHitArea,
  outTrayStackLayout,
  pointStackStep,
  waitStackStep,
} from './backgammonLayout';
import { RESIGN_RESPONSE_TIMEOUT_MS, ResignLifecycle } from './resignLifecycle';

const WIN_GREEN = 0x4ade80;
const ORANGE = 0xd9722e;

// Chrome lives in a fixed-width vertical RAIL down the LEFT edge; the board fills everything to its
// right. Pods stack vertically in the rail (opponent top, me bottom) with the menu + chat + refresh
// cluster in the gap between them. Avatar / name / score / time chip are centered in the narrow rail.
const PAD = 12;
const POD_AVATAR = 72;
const POD_AV_TOP = 12; // avatar inset from the pod's top edge
const POD_H = 208; // vertical pod: avatar over name over score over colour chip over time chip
const TURN_RING_R = POD_AVATAR / 2 + 8; // 44

const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v));
const other = (r: Role): Role => (r === Role.Player1 ? Role.Player2 : r === Role.Player2 ? Role.Player1 : Role.None);
const isOutRow = (row: number): boolean => row === BACKGAMMON_OUT_ROW_WHITE || row === BACKGAMMON_OUT_ROW_BLACK;

// Landscape board (the app auto-rotates the whole scene 90° to fill a portrait-held phone). The board
// fills the viewport edge-to-edge: 24 points along the TOP and BOTTOM long edges (12 each), a VERTICAL
// center bar (holds the WAIT/hit rows), and narrow case wells at the far LEFT and RIGHT edges. The RIGHT
// well is the shared bearing-off tray; the left remains decorative. Checkers stack VERTICALLY inward
// toward the horizontal center line (spineY); the seat mirror is on Y.
interface BoardLayout {
  bx: number;
  by: number;
  bw: number; // = viewport.w (variable long axis)
  bh: number; // = viewport.h (fixed short axis ≈ 720)
  frameW: number; // dark-walnut frame band thickness wrapping the playing field (the "wooden box")
  innerX: number; // bx + frameW — left edge of the framed inner content
  innerW: number; // bw - 2*frameW — width of the framed inner content
  barX: number; // left edge of the vertical center bar
  barW: number; // center-bar width (holds the WAIT/hit rows)
  barCenterX: number; // bx + bw/2
  trayW: number; // left/right case-well width; the right well is the shared bearing-off tray
  leftHalfX: number; // inner-left edge of the left playable half (= innerX + trayW)
  rightHalfX: number; // inner-left edge of the right playable half (= barX + barW)
  halfW: number; // width of one playable half (6 point columns)
  bandW: number; // halfW / 6 — width of one point column
  topEdgeY: number; // by + frameW — top long edge points sit on (inside the frame)
  botEdgeY: number; // by + bh - frameW — bottom long edge (inside the frame)
  spineY: number; // by + bh/2 — horizontal center line & seat mirror axis
  d: number; // checker diameter
  pointLen: number; // triangle apex length (inward, vertical)
  stackRegion: number; // inward length available for compressed stacking
  rightTrayCenterX: number;
}

interface CheckerVisual {
  container: Container;
  faceView: Container;
  sideView: Container;
}

/** A persistent checker sprite (Unity-parity): it keeps its identity across moves and glides to its
 *  recomputed slot every frame, so a move animates the actual checker rather than a clone. */
interface CheckerSprite extends CheckerVisual {
  color: BeadColor;
  row: number; // current engine row (0..23, WAIT_*, OUT_*)
  num: number; // stack index (0 = bottom) — drives target position & zIndex
  targetX: number;
  targetY: number;
}

/**
 * The Backgammon (تخته نرد) table. Unlike Hokm/Blackjack, this runs the SHARED engine locally and
 * optimistically (src/game/backgammon): it computes legal-move suggestions, applies its own and the
 * opponent's moves, and keeps board state between full GetGame snapshots. Interaction mirrors the
 * Unity client exactly — tap a checker → engine.showAvailableOptions highlights legal destinations →
 * tap one → engine.moveBead + send Move(29). Same chrome (pods, timer, chat, resign menu) as the
 * other tables; the board art is API-generated and the checkers/dice/highlights are procedural.
 */
export class BackgammonTableScene extends Scene {
  private engine = new BackgammonLogic();
  private table: TableTextures = {};
  private chatEnabled = true;

  private isGameLoaded = false;
  private turn: Role = Role.None;
  private p1TotalWin = 0;
  private p2TotalWin = 0;
  private time: TimeBlock | null = null;
  private selectedRow: number | null = null;
  private highlights: HighLightColumnDto[] = [];
  private readonly resignFlow = new ResignLifecycle();
  private preserveResignFeedback = false;

  private readonly myRole: Role;
  private readonly isPreview: boolean;
  private readonly seat: GameInfoSeat;

  private readonly bgLayer = new Container();
  private readonly feltLayer = new Container();
  private readonly chrome = new Container();
  private readonly board = new Container();
  private readonly pieces = new Container(); // persistent checker pool (never cleared by render())
  private readonly fx = new Container();
  private readonly overlay = new Container();
  private chat!: ChatControl;
  private timerHandle: TweenHandle | null = null;
  private checkers: CheckerSprite[] = [];
  private snapNext = false; // one-shot: place checkers instantly (rebuild/resize) instead of gliding
  private animateDiceNextRender = false;

  private ly: BoardLayout = this.computeLayout();

  constructor(ctx: AppContext, seat: GameInfoSeat) {
    super();
    this.ctx = ctx;
    this.seat = seat;
    this.myRole = seat.myRole;
    this.isPreview = seat.isPreview;
    // Retarget the moved checker on every bead transfer the engine performs (our moves and mirrored
    // opponent moves) — the persistent sprite then glides to its new slot (Unity-parity, no clones).
    this.engine.onBeadTransfer = (from, last, to) => this.onTransfer(from, last, to);
  }

  private readonly ctx: AppContext;

  /** Landscape: the app rotates the world 90° to fill a portrait-held phone (PixiApp Case A/B). */
  override get orientation(): 'portrait' | 'landscape' {
    return 'landscape';
  }

  onEnter(): void {
    this.addChild(this.bgLayer, this.feltLayer, this.chrome, this.board, this.pieces, this.fx, this.overlay);
    this.pieces.sortableChildren = true; // zIndex = stack num, so higher checkers draw on top
    this.rebuildBg();

    // Prompt the player to turn the device sideways; auto-dismisses (see rotateHint for the locked-
    // webview caveat). Tear down on leave so the DOM node never leaks across scene swaps.
    showRotateHint();
    this.track(() => hideRotateHint());

    // Per-frame checker animation (port of Unity BeadCode.Update MoveTowards). Self-removed on leave.
    const tick = (): void => this.tickCheckers();
    Ticker.shared.add(tick);
    this.track(() => Ticker.shared.remove(tick));

    this.chat = new ChatControl({
      respackFrame: (n) => this.ctx.respackFrame(n),
      onSend: (text) => this.sendChat(text),
      mySide: { x: this.railCx, y: this.meAvCy + POD_AVATAR / 2 },
      oppSide: { x: this.railCx, y: this.oppAvCy + POD_AVATAR / 2 },
    });
    this.fx.addChild(this.chat);

    initSound();
    this.buildChrome();

    this.track(this.ctx.client.on(MessageTag.GetGame, (r) => this.onGetGame(decodeGetGame(r))));
    this.track(this.ctx.client.on(MessageTag.GamePreview, (r) => this.onGetGame(decodeGetGame(r))));
    this.track(this.ctx.client.on(MessageTag.StartGamePanel, (r) => this.onStartGamePanel(r)));
    this.track(this.ctx.client.on(MessageTag.RollDice, (r) => this.onRollDice(decodeRollDice(r))));
    this.track(this.ctx.client.on(MessageTag.Move, (r) => this.onMove(decodeMoveEcho(r))));
    this.track(this.ctx.client.on(MessageTag.SwitchTurn, (r) => this.onSwitchTurn(decodeSwitchTurn(r))));
    this.track(this.ctx.client.on(MessageTag.Undo, () => this.onUndo()));
    this.track(this.ctx.client.on(MessageTag.InGameMessage, (r) => this.onInGameMessage(decodeInGameMessage(r))));
    this.track(this.ctx.client.on(MessageTag.ShowEndGameResult, (r) => this.onEndGame(decodeShowEndGameResult(r))));
    this.track(this.ctx.client.on(MessageTag.Disconnect, (r) => this.onDisconnect(decodeDisconnect(r))));
    this.track(this.ctx.client.on(MessageTag.Chat, (r) => this.onChat(decodeChat(r))));

    void loadTableTextures()
      .then((tex) => {
        this.table = tex;
        this.buildFelt();
        // Checkers spawned before the bead textures finished loading are procedural; rebuild the pool so
        // they re-spawn with the (now-available) wood textures instead of staying the fallback look.
        this.clearCheckers();
        this.snapNext = true;
        this.render();
      })
      .catch((err: unknown) => console.warn('[backgammon] table art failed to load', err));

    this.ctx.client.send(encodeReadyToPlay());
    this.render();
  }

  onResize(): void {
    this.rebuildBg();
    this.buildFelt();
    this.buildChrome();
    this.snapNext = true; // positions jump to the new layout, don't glide
    this.render();
  }

  private rebuildBg(): void {
    for (const ch of this.bgLayer.removeChildren()) ch.destroy({ children: true });
    this.bgLayer.addChild(sceneBackground());
  }

  private computeLayout(): BoardLayout {
    const railW = this.railW;
    const bx = railW; // the board starts at the right edge of the left chrome rail
    const by = 0;
    const bw = viewport.w - railW; // fills everything right of the rail (no right margin)
    const bh = viewport.h; // fixed short axis (≈720)

    // A dark-walnut frame band wraps the playing field on all four sides (the "wooden box"); the trays,
    // halves and center bar all live INSIDE it, and the points sit on the inner long edges.
    const frameW = clamp(Math.round(Math.min(bw, bh) * 0.03), 14, 30);
    const innerX = bx + frameW;
    const innerW = bw - 2 * frameW;
    const topEdgeY = by + frameW;
    const botEdgeY = by + bh - frameW;
    const fieldH = botEdgeY - topEdgeY;

    // Estimate the checker diameter from the inner playable width (~6 columns per half, ≈82.5% of width
    // after reserving the center bar + two side trays), capped by half the field height.
    const estHalfW = (innerW * 0.825) / 2;
    const d = Math.round(Math.min((estHalfW / 6) * CHECKER_COLUMN_FILL, (fieldH / 2) * 0.3));

    // Size the bar/trays to comfortably hold a checker column, then derive the real half/band widths.
    const barW = clamp(Math.round(innerW * 0.055), d + 14, 130);
    const trayW = clamp(Math.round(innerW * 0.06), Math.max(Math.round(d * 0.55), 64), 130);
    const halfW = (innerW - barW - 2 * trayW) / 2;
    const bandW = halfW / 6;

    const leftHalfX = innerX + trayW;
    const barX = innerX + trayW + halfW;
    const rightHalfX = barX + barW;
    const pointLen = Math.round((fieldH / 2) * 0.84); // triangle apex toward the horizontal center

    const layout: BoardLayout = {
      bx,
      by,
      bw,
      bh,
      frameW,
      innerX,
      innerW,
      barX,
      barW,
      barCenterX: bx + bw / 2,
      trayW,
      leftHalfX,
      rightHalfX,
      halfW,
      bandW,
      topEdgeY,
      botEdgeY,
      spineY: by + bh / 2,
      d,
      pointLen,
      stackRegion: Math.max(0, pointLen - d - CHECKER_EDGE_INSET),
      rightTrayCenterX: innerX + innerW - trayW / 2,
    };
    this.ly = layout;
    return layout;
  }

  // ---- chrome anchors: a fixed-width left rail holding the two vertical pods + the menu cluster ----

  private get railW(): number {
    return clamp(Math.round(viewport.h * 0.23), 150, 200); // ≈166 @ h=720
  }
  private get podW(): number {
    return this.railW - 2 * PAD;
  }
  /** Both avatars are centered horizontally in the rail; they differ only in Y (opp top, me bottom). */
  private get railCx(): number {
    return this.railW / 2;
  }
  private get oppPodY(): number {
    return PAD;
  }
  private get mePodY(): number {
    return viewport.h - PAD - POD_H;
  }
  private get oppAvCy(): number {
    return this.oppPodY + POD_AV_TOP + POD_AVATAR / 2;
  }
  private get meAvCy(): number {
    return this.mePodY + POD_AV_TOP + POD_AVATAR / 2;
  }

  // ---- derived colour/role helpers ----

  private get myColor(): BeadColor {
    return this.myRole === Role.Player1 ? this.engine.p1Color : this.engine.p2Color;
  }
  private get oppColor(): BeadColor {
    return this.myRole === Role.Player1 ? this.engine.p2Color : this.engine.p1Color;
  }
  private get flip(): boolean {
    // Unflipped layout places White's home (rows 18..23) bottom-right; mirror about the horizontal
    // center line for Black so the local player's home is always bottom-right.
    return this.myColor === BeadColor.Black;
  }
  private flipY(y: number): number {
    return this.flip ? 2 * this.ly.spineY - y : y;
  }
  private get myWaitRow(): number {
    return this.myColor === BeadColor.White ? BACKGAMMON_WAIT_ROW_WHITE : BACKGAMMON_WAIT_ROW_BLACK;
  }

  // ---- static chrome (mirrors Hokm/Blackjack) ----

  private buildFelt(): void {
    this.feltLayer.removeChildren().forEach((c) => c.destroy());
    this.computeLayout();
    const { bx, by, bw, bh, leftHalfX, rightHalfX, halfW, topEdgeY, botEdgeY, spineY } = this.ly;
    const fieldH = botEdgeY - topEdgeY;

    // The DARK WALNUT box fills the whole board rect; the lighter maple field is drawn inset in the two
    // playable halves on top — so the walnut shows through as the outer frame, the center bar and the
    // side trays (the classic wooden-backgammon-box contrast). Geometry stays procedural on top.
    const walnut = this.table.frame;
    const maple = this.table.board ?? this.table.table;
    const addWood = (tex: Texture | undefined, x: number, y: number, w: number, h: number, radius: number): void => {
      if (!tex) return;
      const s = new Sprite(tex);
      s.position.set(x, y);
      s.width = w;
      s.height = h;
      const mask = new Graphics().roundRect(x, y, w, h, radius).fill(0xffffff);
      s.mask = mask;
      this.feltLayer.addChild(s, mask);
    };
    addWood(walnut ?? maple, bx, by, bw, bh, 16); // the box (falls back to maple if the walnut is absent)
    addWood(maple, leftHalfX, topEdgeY, halfW, fieldH, 4); // left playing field
    addWood(maple, rightHalfX, topEdgeY, halfW, fieldH, 4); // right playing field

    // Optional decorative center emblem in each half (purely cosmetic; safe if the texture is absent).
    const em = this.table.emblem;
    if (em) {
      const size = Math.min(halfW * 0.44, fieldH * 0.25);
      for (const cx of [leftHalfX + halfW / 2, rightHalfX + halfW / 2]) {
        const sp = new Sprite(em);
        sp.anchor.set(0.5);
        sp.width = size;
        sp.height = size;
        sp.position.set(cx, spineY);
        sp.alpha = 0.16;
        this.feltLayer.addChild(sp);
      }
    }
  }

  private buildChrome(): void {
    this.computeLayout();
    for (const c of this.chrome.removeChildren()) c.destroy({ children: true });
    const H = viewport.h;
    const railW = this.railW;

    // The left rail: a recessed wood panel with a gold edge line so it reads as a side rail separate
    // from the board. The pods (added in render()) sit at the top/bottom; this builds the middle cluster.
    const rail = new Graphics()
      .rect(0, 0, railW, H)
      .fill({ color: COLORS.panel, alpha: 0.92 })
      .moveTo(railW, 0)
      .lineTo(railW, H)
      .stroke({ width: BORDER.thick, color: COLORS.gold, alpha: 0.6 });
    this.chrome.addChild(rail);

    // Game menu — full rail width, vertically centered (top of the middle cluster between the pods).
    const menuW = railW - 2 * PAD;
    const clusterY = Math.round(H / 2 - 56);
    const menu = new Button(this.isPreview ? 'بازگشت' : 'منوی بازی', () => this.menuAction(), {
      width: menuW,
      height: 46,
      fontSize: 18,
      icon: this.isPreview ? (this.ctx.iconTexture('return') ?? null) : null,
    });
    menu.position.set(PAD, clusterY);
    this.chrome.addChild(menu);

    // Refresh + chat — a 64px row just below the menu (clear of both pods).
    const rowY = clusterY + 56;
    const refresh = this.railButton('', () => this.ctx.client.send(encodeRefresh()), this.ctx.iconTexture('refresh-page-option'));
    refresh.position.set(PAD, rowY);
    this.chrome.addChild(refresh);
    const chatBtn = this.railButton('گپ', () => this.toggleChat(), this.ctx.respackFrame('chat'));
    chatBtn.position.set(railW - PAD - 64, rowY);
    this.chrome.addChild(chatBtn);
  }

  private railButton(label: string, onTap: () => void, tex?: Texture): Container {
    const c = new Container();
    const w = 64;
    const h = 64;
    const g = new Graphics().roundRect(0, 0, w, h, 12).fill({ color: ORANGE });
    c.addChild(g);
    if (tex) {
      const icon = iconSprite(tex, label ? 34 : 44);
      icon.position.set((w - (label ? 34 : 44)) / 2, label ? 6 : 10);
      c.addChild(icon);
    }
    if (label) {
      const t = makeText(label, { size: 20, weight: '700', align: 'center', fill: COLORS.white });
      t.anchor.set(0.5);
      t.position.set(w / 2, tex ? h - 16 : h / 2);
      c.addChild(t);
    }
    c.hitArea = new Rectangle(0, 0, w, h);
    c.eventMode = 'static';
    c.cursor = 'pointer';
    c.on('pointertap', onTap);
    c.on('pointerover', () => (c.alpha = 0.85));
    c.on('pointerout', () => (c.alpha = 1));
    return c;
  }

  // ---- incoming messages ----

  private onGetGame(state: ReturnType<typeof decodeGetGame>): void {
    if (this.resignFlow.isTerminal) return;
    const preserveOverlay = this.consumeResignFeedback();
    this.engine = state.logic;
    this.engine.onBeadTransfer = (from, last, to) => this.onTransfer(from, last, to);
    this.p1TotalWin = state.player1TotalWin;
    this.p2TotalWin = state.player2TotalWin;
    this.turn = state.turn;
    this.time = state.time;
    this.isGameLoaded = true;
    this.selectedRow = null;
    this.highlights = [];
    // Engine replaced wholesale (authoritative resync / round reset) — rebuild the pool and snap so
    // checkers don't glide from stale positions.
    this.clearCheckers();
    this.snapNext = true;
    if (!preserveOverlay) this.clearOverlay();
    this.render();
  }

  private onStartGamePanel(r: BinaryReader): void {
    if (this.resignFlow.isTerminal) return;
    // Intro packet (who rolls first + opening dice). The board itself comes via GetGame, so pull it.
    const intro = decodeStartGamePanel(r);
    this.p1TotalWin = intro.info.player1TotalWin;
    this.p2TotalWin = intro.info.player2TotalWin;
    this.banner(intro.winner === this.myRole ? 'شما شروع می‌کنید' : 'حریف شروع می‌کند', COLORS.gold);
    this.ctx.client.send(encodeGetGame());
  }

  private onRollDice(p: ReturnType<typeof decodeRollDice>): void {
    if (this.resignFlow.isTerminal) return;
    this.engine.dice = p.dice;
    this.time = p.time;
    this.selectedRow = null;
    this.highlights = [];
    this.animateDiceNextRender = true;
    playSfx('backgammon-dice');
    this.render();
  }

  private onMove(p: ReturnType<typeof decodeMoveEcho>): void {
    if (this.resignFlow.isTerminal) return;
    if (this.isGameLoaded) {
      playSfx('backgammon-move');
      // "do not echo my moves": only apply when it's NOT my turn (i.e. the opponent moved), or preview.
      const shouldShowMove = this.turn !== this.myRole || this.isPreview;
      if (p.moves.length && shouldShowMove) {
        for (const mp of p.moves) {
          if (this.engine.availableRows().includes(mp.start)) {
            this.engine.moveBead(mp.start, mp.dices);
          }
        }
      }
      this.p1TotalWin = p.player1TotalWin;
      this.p2TotalWin = p.player2TotalWin;
      this.time = p.time;
      this.switchTurnIfAllDiceDone();
    }
    this.selectedRow = null;
    this.highlights = [];
    this.render();
  }

  private onSwitchTurn(p: ReturnType<typeof decodeSwitchTurn>): void {
    if (this.resignFlow.isTerminal) return;
    this.switchTurn();
    this.engine.dice.readyToThrowDice();
    this.p1TotalWin = p.player1TotalWin;
    this.p2TotalWin = p.player2TotalWin;
    this.time = p.time;
    this.selectedRow = null;
    this.highlights = [];
    this.render();
  }

  private onUndo(): void {
    if (this.resignFlow.isTerminal) return;
    // The server relays Undo to the OPPONENT only — mirror their undo on our local engine.
    if (!this.engine.undoMove()) {
      this.banner('حرکتی برای برگشت وجود ندارد', COLORS.textDim);
    }
    this.selectedRow = null;
    this.highlights = [];
    this.render();
  }

  private onInGameMessage(text: string): void {
    if (this.resignFlow.isTerminal) return;
    if (this.resignFlow.acknowledge()) {
      this.clearOverlay();
      this.preserveResignFeedback = Boolean(text);
    }
    if (text) this.banner(text, COLORS.gold);
    this.selectedRow = null;
    this.highlights = [];
    this.render();
  }

  private onEndGame(eg: ReturnType<typeof decodeShowEndGameResult>): void {
    if (!this.resignFlow.complete()) return;
    this.showEndGame(eg.resultText, eg.reasonText);
  }

  private onDisconnect(d: ReturnType<typeof decodeDisconnect>): void {
    if (this.resignFlow.isTerminal) return;
    this.banner(d.isLeaving ? 'حریف از بازی خارج شد' : 'حریف به بازی بازگشت', d.isLeaving ? COLORS.danger : COLORS.gold);
  }

  private onChat(payload: ReturnType<typeof decodeChat>): void {
    if (this.resignFlow.isTerminal || !this.chatEnabled) return;
    const side = payload.senderRole === this.myRole ? 'me' : 'opp';
    this.chat.showBubble(side, payload.text);
  }

  // ---- local turn bookkeeping (ports BackGammon.cs) ----

  private switchTurn(): void {
    this.turn = other(this.turn);
    this.engine.switchTurn();
  }

  private switchTurnIfAllDiceDone(): void {
    if (this.engine.dice.allDone) {
      this.switchTurn();
      this.engine.dice.readyToThrowDice();
    }
  }

  // ---- actions ----

  private rollDice(): void {
    if (this.resignFlow.isPending || this.resignFlow.isTerminal) return;
    this.ctx.client.send(encodeRollDice());
  }

  private selectSource(row: number): void {
    if (this.resignFlow.isPending || this.resignFlow.isTerminal) return;
    if (this.selectedRow === row) {
      this.selectedRow = null;
      this.highlights = [];
      this.render();
      return;
    }
    this.highlights = this.engine.showAvailableOptions(row);
    this.selectedRow = row;
    this.render();
  }

  private playTo(dto: HighLightColumnDto): void {
    if (this.resignFlow.isPending || this.resignFlow.isTerminal) return;
    if (this.selectedRow === null) return;
    const moves = this.engine.moveBead(this.selectedRow, dto.diceNeed);
    if (!moves.length) {
      this.selectedRow = null;
      this.highlights = [];
      this.render();
      return;
    }
    this.switchTurnIfAllDiceDone();
    this.ctx.client.send(encodeMove(moves));
    this.selectedRow = null;
    this.highlights = [];
    this.render();
  }

  private undo(): void {
    if (this.resignFlow.isPending || this.resignFlow.isTerminal) return;
    if (this.engine.undoMove()) {
      this.ctx.client.send(encodeUndo());
      this.selectedRow = null;
      this.highlights = [];
      this.render();
    } else {
      this.banner('حرکتی برای برگشت وجود ندارد', COLORS.textDim);
    }
  }

  private sendChat(text: string): void {
    this.ctx.client.send(encodeChat(text));
    this.chat.showBubble('me', text);
  }

  private toggleChat(): void {
    if (this.chatEnabled) this.chat.toggle();
    else this.banner('چت غیرفعال است', COLORS.textDim);
  }

  // ---- rendering ----

  private render(): void {
    this.computeLayout();
    this.timerHandle?.cancel();
    this.timerHandle = null;
    for (const child of this.board.removeChildren()) child.destroy({ children: true });

    this.renderPods();

    if (!this.isGameLoaded) {
      this.clearCheckers();
      this.board.addChild(this.centered('در انتظار شروع بازی…', viewport.h / 2));
      return;
    }

    this.renderBoardFrame();
    this.renderPoints();
    this.reconcileCheckers(this.snapNext); // checkers live in the persistent `pieces` layer
    this.snapNext = false;
    this.applyCheckerDimming(); // gray out beads with no legal move (movable checkers stay lit)
    if (this.selectedRow !== null) this.renderSelection();
    this.renderSourceHitZones(); // interactive, above checkers
    this.renderHighlights(); // interactive, above the source zones
    this.renderControls();
    this.renderTimer();
  }

  private renderPods(): void {
    const p1 = this.seat.player1;
    const p2 = this.seat.player2;
    const meIsP1 = this.myRole === Role.Player1;
    const me = meIsP1 ? p1 : p2;
    const opp = meIsP1 ? p2 : p1;
    const myWins = meIsP1 ? this.p1TotalWin : this.p2TotalWin;
    const oppWins = meIsP1 ? this.p2TotalWin : this.p1TotalWin;
    const myBank = (meIsP1 ? this.time?.player1TimeBank : this.time?.player2TimeBank) ?? 0;
    const oppBank = (meIsP1 ? this.time?.player2TimeBank : this.time?.player1TimeBank) ?? 0;
    const total = this.seat.game.gameCount;
    const meActive = this.isGameLoaded && this.turn === this.myRole;
    const oppActive = this.isGameLoaded && this.turn === other(this.myRole);

    this.board.addChild(this.playerPod('me', { avatar: me.avatar, name: me.username, timeBank: myBank, roundWins: myWins, totalRounds: total, color: this.myColor, active: meActive }));
    this.board.addChild(this.playerPod('opp', { avatar: opp.avatar, name: opp.username, timeBank: oppBank, roundWins: oppWins, totalRounds: total, color: this.oppColor, active: oppActive }));
  }

  private renderTimer(): void {
    if (!this.time) return;
    const activeIsMe = this.turn === this.myRole;
    const cy = activeIsMe ? this.meAvCy : this.oppAvCy;
    const meIsP1 = this.myRole === Role.Player1;
    const bank = activeIsMe
      ? (meIsP1 ? this.time.player1TimeBank : this.time.player2TimeBank)
      : (meIsP1 ? this.time.player2TimeBank : this.time.player1TimeBank);
    this.timerHandle = this.turnTimer(this.railCx, cy, this.time.turnNextTrigger, bank);
  }

  // ---- board geometry (vertical / portrait) ----

  /** Engine row → which long edge (top/bottom), which half (left/right), and the column index k (0 =
   *  outer column nearest that half's tray … 5 = nearest the center bar). Screen-true (unflipped): the
   *  perimeter ring runs continuously 12→23 across the bottom, up the right side (23↔0), 0→11 across the
   *  top, and down the left side (11↔12). White's home (rows 18..23) lands bottom-right. */
  private rawSlot(row: number): { colSide: 'left' | 'right'; edge: 'top' | 'bot'; k: number } {
    if (row <= 5) return { colSide: 'right', edge: 'top', k: 5 - row }; // pt 1..6 (Black home), top-right
    if (row <= 11) return { colSide: 'left', edge: 'top', k: 11 - row }; // pt 7..12, top-left
    if (row <= 17) return { colSide: 'left', edge: 'bot', k: row - 12 }; // pt 13..18, bottom-left
    return { colSide: 'right', edge: 'bot', k: row - 18 }; // pt 19..24 (White home), bottom-right
  }

  /** Inward spacing between successive checkers in a stack: gently overlapping when shallow and
   *  compressed just enough to keep deep stacks inside the point. */
  private calcStep(count: number): number {
    return pointStackStep(this.ly.d, this.ly.stackRegion, count);
  }

  private outTrayLayout(count: number) {
    return outTrayStackLayout(
      this.ly.d,
      this.ly.trayW,
      this.ly.botEdgeY - this.ly.spineY,
      count,
    );
  }

  private rowCount(row: number): number {
    if (row >= 0 && row < 24) return this.engine.beads.gameRows[row].count;
    if (row === BACKGAMMON_WAIT_ROW_WHITE) return this.engine.beads.waitRow.get(BeadColor.White)!.count;
    if (row === BACKGAMMON_WAIT_ROW_BLACK) return this.engine.beads.waitRow.get(BeadColor.Black)!.count;
    if (row === BACKGAMMON_OUT_ROW_WHITE) return this.engine.beads.outRow.get(BeadColor.White)!.count;
    if (row === BACKGAMMON_OUT_ROW_BLACK) return this.engine.beads.outRow.get(BeadColor.Black)!.count;
    return 0;
  }

  private rowColor(row: number): BeadColor {
    if (row >= 0 && row < 24) return this.engine.beads.gameRows[row].color;
    if (row === BACKGAMMON_WAIT_ROW_WHITE || row === BACKGAMMON_OUT_ROW_WHITE) return BeadColor.White;
    return BeadColor.Black; // WAIT_ROW_BLACK / OUT_ROW_BLACK
  }

  /** Center (x,y) of the checker at `stackIndex` in a stack of `stackCount`, for any row (game point,
   *  bar, or tray). Applies the seat mirror-Y flip. INPUT row is always a TRUE engine index. */
  private rowToScreen(row: number, stackIndex: number, stackCount: number): { x: number; y: number } {
    const { d, spineY, topEdgeY, botEdgeY, barCenterX, rightTrayCenterX, bandW } = this.ly;

    // WAIT rows (vertical center bar) — stack vertically outward from the center toward the owner's side.
    if (row === BACKGAMMON_WAIT_ROW_WHITE || row === BACKGAMMON_WAIT_ROW_BLACK) {
      const mine = row === this.myWaitRow;
      const sgn = mine ? 1 : -1; // mine below the center line, opponent above (then flipY per seat)
      const step = waitStackStep(d, botEdgeY - spineY, stackCount);
      const y = spineY + sgn * (d + stackIndex * step);
      return { x: barCenterX, y: this.flipY(y) };
    }

    // Shared OUT tray (right column): Black owns the top half and White the bottom in raw board space.
    // Seat mirroring therefore always puts the local player's pieces in the lower half. Both stacks
    // grow inward from their outer field edges toward the tray's center seam.
    if (isOutRow(row)) {
      const layout = this.outTrayLayout(stackCount);
      const offset = layout.centerOffsets[stackIndex] ?? 6 + layout.pieceHeight / 2;
      const y = row === BACKGAMMON_OUT_ROW_BLACK ? topEdgeY + offset : botEdgeY - offset;
      return { x: rightTrayCenterX, y: this.flipY(y) };
    }

    // Game points — base on the long edge, stack inward toward the horizontal center line.
    const { colSide, edge, k } = this.rawSlot(row);
    const cx = (colSide === 'left' ? this.ly.leftHalfX : this.ly.rightHalfX) + (k + 0.5) * bandW;
    const step = this.calcStep(stackCount);
    const inset = CHECKER_EDGE_INSET;
    const y =
      edge === 'top'
        ? topEdgeY + d / 2 + inset + stackIndex * step
        : botEdgeY - d / 2 - inset - stackIndex * step;
    return { x: cx, y: this.flipY(y) };
  }

  private renderBoardFrame(): void {
    const { bx, bw, by, bh, barX, barW, trayW, innerX, innerW, topEdgeY, botEdgeY, spineY, leftHalfX, rightHalfX, halfW } = this.ly;
    const hasWalnut = !!this.table.frame;
    const fieldH = botEdgeY - topEdgeY;
    const g = new Graphics();

    // Fallback wood box only when the walnut texture hasn't loaded (else buildFelt already painted it).
    if (!hasWalnut) {
      g.roundRect(bx, by, bw, bh, 16).fill({ color: 0x3a2410 });
    }

    // Each playable half: a carved dark inner shadow + gold lip so the maple field reads as inset wood.
    const carve = (x: number, y: number, w: number, h: number): void => {
      g.roundRect(x, y, w, h, 5).stroke({ width: 2, color: 0x140c04, alpha: 0.38 });
      g.roundRect(x - 1.5, y - 1.5, w + 3, h + 3, 6).stroke({ width: 1.5, color: COLORS.gold, alpha: 0.28 });
    };
    carve(leftHalfX, topEdgeY, halfW, fieldH);
    carve(rightHalfX, topEdgeY, halfW, fieldH);

    // The right well is the shared bearing-off tray. The matching left well remains as decorative
    // casework so the playable points retain their established proportions.
    const trayWell = (x: number): void => {
      g.roundRect(x, topEdgeY, trayW, fieldH, 6).fill({ color: 0x261607, alpha: hasWalnut ? 0.34 : 0.92 });
      g.roundRect(x + 1, topEdgeY + 1, trayW - 2, fieldH - 2, 5).stroke({ width: 2, color: 0x120a03, alpha: 0.48 });
      g.roundRect(x, topEdgeY, trayW, fieldH, 6).stroke({ width: 1.5, color: COLORS.gold, alpha: 0.28 });
    };
    trayWell(innerX);
    const rightTrayX = innerX + innerW - trayW;
    trayWell(rightTrayX);
    // Quiet seam separating the two players' halves of the shared tray.
    g.moveTo(rightTrayX + 5, spineY).lineTo(rightTrayX + trayW - 5, spineY).stroke({ width: 3, color: 0x120a03, alpha: 0.58 });
    g.moveTo(rightTrayX + 6, spineY - 1).lineTo(rightTrayX + trayW - 6, spineY - 1).stroke({ width: 1, color: COLORS.gold, alpha: 0.42 });

    // Raised center bar: a proud walnut divider with bevel highlight/shadow + two brass hinge clasps.
    g.rect(barX, topEdgeY, barW, fieldH).fill({ color: 0x2a1808, alpha: hasWalnut ? 0.2 : 0.88 });
    g.moveTo(barX + 1, topEdgeY).lineTo(barX + 1, botEdgeY).stroke({ width: 2, color: 0xf3e9d2, alpha: 0.16 }); // left highlight
    g.moveTo(barX + barW - 1, topEdgeY).lineTo(barX + barW - 1, botEdgeY).stroke({ width: 2, color: 0x120a03, alpha: 0.5 }); // right shadow
    g.rect(barX, topEdgeY, barW, fieldH).stroke({ width: 2, color: COLORS.gold, alpha: 0.28 });
    const hingeH = Math.min(34, fieldH * 0.06);
    for (const hy of [topEdgeY + fieldH * 0.05, botEdgeY - fieldH * 0.05 - hingeH]) {
      g.roundRect(barX + barW * 0.18, hy, barW * 0.64, hingeH, 3).fill({ color: 0xc79a44 }).stroke({ width: 1, color: 0x6b4e12, alpha: 0.85 });
      g.circle(barX + barW * 0.34, hy + hingeH / 2, 2.2).fill({ color: 0x3a2a0e });
      g.circle(barX + barW * 0.66, hy + hingeH / 2, 2.2).fill({ color: 0x3a2a0e });
    }

    // Outer box rim: gold edge + soft cream inner bevel + a dark line where the frame meets the content.
    g.roundRect(bx, by, bw, bh, 16).stroke({ width: BORDER.thick + 1, color: COLORS.gold, alpha: 0.7 });
    g.roundRect(bx + 2.5, by + 2.5, bw - 5, bh - 5, 14).stroke({ width: 1.5, color: 0xf3e9d2, alpha: 0.16 });
    g.roundRect(innerX, topEdgeY, innerW, fieldH, 8).stroke({ width: 2, color: 0x120a03, alpha: 0.42 });
    this.board.addChild(g);
  }

  private renderPoints(): void {
    const { bandW, pointLen, topEdgeY, botEdgeY } = this.ly;
    const g = new Graphics();
    for (let row = 0; row < 24; row++) {
      const { colSide, edge, k } = this.rawSlot(row);
      const cx = (colSide === 'left' ? this.ly.leftHalfX : this.ly.rightHalfX) + (k + 0.5) * bandW;
      const halfBase = bandW * 0.42;
      const light = row % 2 === 0;
      const fill = light ? 0xe5d3b3 : COLORS.gameBackgammon; // warm ivory / deep petrol blue
      const sheen = light ? 0xf4ead7 : 0x2b5a70;
      const baseY = edge === 'top' ? topEdgeY : botEdgeY;
      const apexY = edge === 'top' ? topEdgeY + pointLen : botEdgeY - pointLen;
      const bY = this.flipY(baseY);
      const aY = this.flipY(apexY);
      // body
      g.moveTo(cx - halfBase, bY).lineTo(cx + halfBase, bY).lineTo(cx, aY).closePath();
      g.fill({ color: fill, alpha: light ? 0.9 : 0.88 });
      // Restrained inlay sheen down the spine; the wood grain remains visible beneath it.
      const midY = bY + (aY - bY) * 0.5;
      g.moveTo(cx - halfBase * 0.52, bY).lineTo(cx + halfBase * 0.52, bY).lineTo(cx, midY).closePath();
      g.fill({ color: sheen, alpha: 0.18 });
      // A quiet seam replaces the previous gold outline and keeps the points distinct without clutter.
      g.moveTo(cx - halfBase, bY).lineTo(cx, aY).lineTo(cx + halfBase, bY).stroke({ width: 1, color: 0x101820, alpha: 0.2 });
    }
    this.board.addChild(g);
  }

  // ---- persistent checker pool + animation (Unity-parity, no clones) ----

  private clearCheckers(): void {
    for (const c of this.checkers) if (!c.container.destroyed) c.container.destroy();
    this.checkers = [];
  }

  private spawnChecker(color: BeadColor): CheckerSprite {
    const visual = this.makeChecker(color, this.ly.d);
    this.pieces.addChild(visual.container);
    const cs: CheckerSprite = { ...visual, color, row: -999, num: 0, targetX: 0, targetY: 0 };
    this.checkers.push(cs);
    return cs;
  }

  private setCheckerSideView(checker: CheckerSprite, showSide: boolean): void {
    checker.faceView.visible = !showSide;
    checker.sideView.visible = showSide;
  }

  /** Make the persistent pool match the engine across all rows, preserving sprite identity (so a moved
   *  checker keeps gliding rather than popping). `snap` places sprites instantly (rebuild/resize). */
  private reconcileCheckers(snap = false): void {
    interface Slot {
      row: number;
      color: BeadColor;
      count: number;
    }
    const slots: Slot[] = [];
    for (let r = 0; r < 24; r++) {
      const br = this.engine.beads.gameRows[r];
      if (br.count > 0) slots.push({ row: r, color: br.color, count: br.count });
    }
    for (const color of [BeadColor.White, BeadColor.Black]) {
      const wc = this.engine.beads.waitRow.get(color)!.count;
      if (wc > 0) slots.push({ row: color === BeadColor.White ? BACKGAMMON_WAIT_ROW_WHITE : BACKGAMMON_WAIT_ROW_BLACK, color, count: wc });
      const oc = this.engine.beads.outRow.get(color)!.count;
      if (oc > 0) slots.push({ row: color === BeadColor.White ? BACKGAMMON_OUT_ROW_WHITE : BACKGAMMON_OUT_ROW_BLACK, color, count: oc });
    }

    const pool = this.checkers.filter((c) => !c.container.destroyed);
    const used = new Set<CheckerSprite>();
    for (const s of slots) {
      const have = pool.filter((c) => c.row === s.row && c.color === s.color && !used.has(c)).sort((a, b) => a.num - b.num);
      for (let i = 0; i < s.count; i++) {
        let sprite = have[i];
        const isNew = !sprite;
        if (!sprite) sprite = this.spawnChecker(s.color);
        used.add(sprite);
        sprite.row = s.row;
        sprite.num = i;
        if (snap || isNew) {
          const p = this.rowToScreen(s.row, i, s.count);
          sprite.container.position.set(p.x, p.y);
          this.setCheckerSideView(sprite, isOutRow(s.row));
        }
        sprite.container.zIndex = i + 1;
      }
    }
    for (const c of this.checkers) if (!used.has(c) && !c.container.destroyed) c.container.destroy();
    this.checkers = this.checkers.filter((c) => used.has(c) && !c.container.destroyed);
  }

  /** Engine bead-transfer hook (port of Unity BackGammonBeadTransfer): retarget the ONE moved sprite
   *  (top of source) to the destination's top slot — it then glides there. Fires after the engine has
   *  mutated, so the destination row is non-empty and its colour is valid. */
  private onTransfer(from: number, lastBeadNum: number, to: number): void {
    const movedColor = this.rowColor(to);
    const moved = this.checkers.find(
      (c) => c.row === from && c.num === lastBeadNum && c.color === movedColor && !c.container.destroyed,
    );
    if (moved) {
      moved.row = to;
      moved.num = Math.max(0, this.rowCount(to) - 1);
      // A borne-off checker stays face-up during its glide and turns edge-on only after settling.
      // Undoing a bear-off similarly restores the round face before it leaves the tray.
      this.setCheckerSideView(moved, false);
    }
  }

  /** Per-frame: glide every checker toward its live slot (MoveTowards), and keep zIndex = stack num. */
  private tickCheckers(): void {
    if (!this.checkers.length) return;
    const maxStep = 0.02 * this.ly.d * Ticker.shared.deltaMS;
    for (const c of this.checkers) {
      if (c.container.destroyed) continue;
      const cnt = this.rowCount(c.row) || c.num + 1;
      const t = this.rowToScreen(c.row, c.num, cnt);
      c.targetX = t.x;
      c.targetY = t.y;
      const p = c.container.position;
      const dx = t.x - p.x;
      const dy = t.y - p.y;
      const dist = Math.hypot(dx, dy);
      if (dist <= maxStep || dist === 0) {
        p.set(t.x, t.y);
        if (isOutRow(c.row)) this.setCheckerSideView(c, true);
      } else {
        p.set(p.x + (dx / dist) * maxStep, p.y + (dy / dist) * maxStep);
      }
      if (!isOutRow(c.row)) this.setCheckerSideView(c, false);
      c.container.zIndex = c.num + 1;
    }
  }

  /** Builds both persistent views of a checker: its top face for play and edge-on side for bearing off. */
  private makeChecker(color: BeadColor, d: number): CheckerVisual {
    const c = new Container();
    const r = d / 2;
    const white = color === BeadColor.White;
    const faceView = new Container();
    const sideView = new Container();

    // Prefer the shadow-free v2 pair; a missing texture falls through to the shadow-free procedural art.
    const tex = this.table[white ? 'checker-white-v2' : 'checker-black-v2'];
    if (tex) {
      const sp = new Sprite(tex);
      sp.anchor.set(0.5);
      sp.width = d;
      sp.height = d;
      faceView.addChild(sp);
    } else {
      const g = new Graphics();
      // Shadow-free procedural fallback with the same ivory / dark-walnut / restrained-brass language.
      g.circle(0, 0, r).fill({ color: white ? 0xeee2c8 : 0x2b160d }).stroke({ width: Math.max(1, d * 0.038), color: 0xb38a3e });
      g.circle(0, 0, r * 0.82).fill({ color: white ? 0xf5ead4 : 0x351b10 });
      g.circle(0, 0, r * 0.62).fill({ color: white ? 0xf8efdd : 0x3e2114 });
      g.circle(0, 0, r * 0.62).stroke({ width: Math.max(1, d * 0.025), color: 0xb38a3e, alpha: 0.82 });
      g.circle(-r * 0.24, -r * 0.25, r * 0.14).fill({ color: 0xffffff, alpha: white ? 0.22 : 0.08 });
      faceView.addChild(g);
    }

    const out = this.outTrayLayout(1);
    const sideTex = this.table[white ? 'checker-white-side-v2' : 'checker-black-side-v2'];
    if (sideTex) {
      const sp = new Sprite(sideTex);
      sp.anchor.set(0.5);
      sp.width = out.pieceWidth;
      sp.height = out.pieceHeight;
      sideView.addChild(sp);
    } else {
      const g = new Graphics();
      const x = -out.pieceWidth / 2;
      const y = -out.pieceHeight / 2;
      const radius = Math.max(2, out.pieceHeight * 0.25);
      g.roundRect(x, y, out.pieceWidth, out.pieceHeight, radius)
        .fill({ color: white ? 0xeee2c8 : 0x351b10 })
        .stroke({ width: Math.max(1, out.pieceHeight * 0.12), color: 0xb38a3e });
      g.moveTo(x + radius, y + out.pieceHeight * 0.34)
        .lineTo(x + out.pieceWidth - radius, y + out.pieceHeight * 0.34)
        .stroke({ width: 1, color: white ? 0xffffff : 0x9b633b, alpha: white ? 0.34 : 0.28 });
      sideView.addChild(g);
    }

    sideView.visible = false;
    c.addChild(faceView, sideView);
    return { container: c, faceView, sideView };
  }

  private renderSelection(): void {
    if (this.selectedRow === null) return;
    const row = this.selectedRow;
    const cnt = this.rowCount(row);
    const pos = this.rowToScreen(row, Math.max(0, cnt - 1), Math.max(1, cnt));
    glowRing(this.board, pos.x, pos.y, this.ly.d / 2 + 4, COLORS.gold);
  }

  private renderHighlights(): void {
    for (const dto of this.highlights) {
      const target = dto.targetRow;
      const cnt = this.rowCount(target);
      const pos = this.rowToScreen(target, cnt, cnt + 1); // top of the destination stack after the move
      const r = this.ly.d / 2;
      const marker = new Graphics();
      if (isOutRow(target)) {
        const out = this.outTrayLayout(cnt + 1);
        marker
          .roundRect(-out.pieceWidth / 2, -out.pieceHeight / 2, out.pieceWidth, out.pieceHeight, Math.max(2, out.pieceHeight * 0.25))
          .fill({ color: COLORS.gold, alpha: 0.1 })
          .stroke({ width: 2, color: COLORS.gold, alpha: 0.95 });

        // Keep the next-slot marker as guidance, but make the local player's entire half of the tray
        // tappable. The board is seat-mirrored so the local half is always below the center seam.
        const trayX = this.ly.innerX + this.ly.innerW - this.ly.trayW;
        const area = bearingOffTrayHitArea(trayX, this.ly.trayW, this.ly.spineY, this.ly.botEdgeY);
        const targetControl = new Container();
        targetControl.hitArea = new Rectangle(area.x, area.y, area.width, area.height);
        targetControl.eventMode = 'static';
        targetControl.cursor = 'pointer';
        targetControl.on('pointertap', () => this.playTo(dto));
        marker.position.set(pos.x, pos.y);
        targetControl.addChild(marker);
        pulse(marker, 1.06, 850);
        this.board.addChild(targetControl);
        continue;
      } else {
        marker
          .circle(0, 0, r * 0.9)
          .fill({ color: WIN_GREEN, alpha: 0.08 })
          .circle(0, 0, r * 0.9)
          .stroke({ width: 2.25, color: WIN_GREEN, alpha: 0.92 })
          .circle(0, 0, Math.max(3, r * 0.1))
          .fill({ color: WIN_GREEN, alpha: 0.95 });
        marker.hitArea = new Rectangle(-r, -r, r * 2, r * 2);
      }
      marker.position.set(pos.x, pos.y);
      marker.eventMode = 'static';
      marker.cursor = 'pointer';
      marker.on('pointertap', () => this.playTo(dto));
      pulse(marker, 1.06, 850);
      this.board.addChild(marker);
    }
  }

  /** True while it's my turn to MOVE a checker (dice already rolled, not just-spectating). */
  private isMyTurnToMove(): boolean {
    return !this.isPreview && this.engine.turnColor === this.myColor && !this.engine.dice.isReadyToThrowDice;
  }

  /** Gray out the BEADS with NO legal move this turn so the movable checkers stand out (dim the beads,
   *  not the point columns). Active only while choosing a source (my turn, dice rolled, nothing selected
   *  yet); otherwise every checker is at full alpha. `availableRows()` already encodes the bar rule —
   *  with a checker on the bar it returns only the WAIT row, so only the bar bead stays lit. A checker is
   *  movable iff its row is one of those source rows. */
  private applyCheckerDimming(): void {
    const dimming = this.isMyTurnToMove() && this.selectedRow === null;
    const playable = dimming ? new Set(this.engine.availableRows()) : null;
    for (const c of this.checkers) {
      if (c.container.destroyed) continue;
      c.container.alpha = playable && !playable.has(c.row) ? 0.4 : 1;
    }
  }

  private renderSourceHitZones(): void {
    if (!this.isMyTurnToMove()) return;
    const { d } = this.ly;
    const addStackZone = (
      row: number,
      first: { x: number; y: number },
      last: { x: number; y: number },
      columnWidth: number,
    ): void => {
      const area = checkerStackHitArea(first.x, first.y, last.y, d, columnWidth);
      const z = new Graphics().rect(0, 0, area.width, area.height).fill({ color: 0xffffff, alpha: 0.001 });
      z.position.set(area.x, area.y);
      z.eventMode = 'static';
      z.cursor = 'pointer';
      z.hitArea = new Rectangle(0, 0, area.width, area.height);
      z.on('pointertap', () => this.selectSource(row));
      this.board.addChild(z);
    };

    // If I have checkers on the bar I MUST move them first — only the bar is selectable then.
    const barCount = this.engine.beads.waitRow.get(this.myColor)!.count;
    if (barCount > 0) {
      const first = this.rowToScreen(this.myWaitRow, 0, barCount);
      const last = this.rowToScreen(this.myWaitRow, barCount - 1, barCount);
      addStackZone(this.myWaitRow, first, last, this.ly.barW);
      return;
    }
    for (let row = 0; row < 24; row++) {
      const br = this.engine.beads.gameRows[row];
      if (br.count > 0 && br.color === this.myColor) {
        const first = this.rowToScreen(row, 0, br.count);
        const last = this.rowToScreen(row, br.count - 1, br.count);
        addStackZone(row, first, last, this.ly.bandW);
      }
    }
  }

  // ---- control strip: dice + roll/undo ----

  private renderControls(): void {
    const { leftHalfX, rightHalfX, halfW, barCenterX, spineY, bh } = this.ly;
    const myTurn = this.engine.turnColor === this.myColor && !this.isPreview;
    const activeHalfX = myTurn ? leftHalfX : rightHalfX;
    const activeHalfCenterX = activeHalfX + halfW / 2;

    if (myTurn && this.engine.dice.isReadyToThrowDice) {
      this.animateDiceNextRender = false;
      const rw = Math.min(220, halfW - 32);
      const rh = 64;
      const roll = new Button('تاس بریز', () => this.rollDice(), { width: rw, height: rh, fontSize: 26 });
      roll.position.set(activeHalfCenterX - rw / 2, spineY - rh / 2);
      this.board.addChild(roll);
      scaleIn(roll, 200, 0.8);
      return;
    }

    // Dice live in the open horizontal center of the active player's half: mine on the left, opponent
    // on the right. This keeps the center bar fully available to hit/wait checkers.
    const avail = this.engine.dice.availables;
    const used = this.engine.dice.used;
    const dice = [...avail.map((v) => ({ v, used: false })), ...used.map((v) => ({ v, used: true }))];
    const animateDice = this.animateDiceNextRender;
    this.animateDiceNextRender = false;
    if (dice.length) {
      const strip = backgammonDiceStrip({
        count: dice.length,
        checkerDiameter: this.ly.d,
        halfX: activeHalfX,
        halfWidth: halfW,
        centerY: spineY,
        emphasized: myTurn,
      });

      if (myTurn) {
        const padX = 14;
        const padY = 12;
        const plate = new Graphics()
          .roundRect(
            activeHalfCenterX - strip.width / 2 - padX,
            spineY - strip.dieSize / 2 - padY,
            strip.width + padX * 2,
            strip.dieSize + padY * 2,
            18,
          )
          .fill({ color: 0x170d06, alpha: 0.38 })
          .stroke({ width: 2, color: COLORS.gold, alpha: 0.82 });
        this.board.addChild(plate);
      }

      for (const [index, die] of dice.entries()) {
        const node = this.makeDie(die.v, strip.dieSize, die.used);
        node.pivot.set(strip.dieSize / 2, strip.dieSize / 2);
        const finalX = strip.centers[index].x;
        const finalY = strip.centers[index].y;
        node.position.set(finalX, finalY);
        if (!myTurn) node.alpha *= 0.78;
        this.board.addChild(node);
        if (animateDice && !die.used) {
          this.tumbleDie(node, finalX, finalY, strip.dieSize, index, activeHalfCenterX);
        }
      }
    }

    // undo (only on my turn, after I've moved) — bottom-center, under the bar.
    if (myTurn && this.engine.undo.count() > 0) {
      const undoBtn = this.railButton('برگشت', () => this.undo(), this.ctx.iconTexture('return'));
      undoBtn.position.set(barCenterX - 32, bh - PAD - 64);
      this.board.addChild(undoBtn);
    }
  }

  private makeDie(value: number, size: number, used: boolean): Container {
    const c = new Container();
    const faceTop = used ? 0x9a8c70 : 0xfdf6e6; // lighter upper tone …
    const faceBot = used ? 0x7c7058 : 0xe9d8b3; // … over a warmer lower tone (a soft vertical gradient)
    const pipColor = used ? 0x4a4234 : 0x241808;
    const radius = size * 0.24;
    const g = new Graphics();
    // Dice retain only a restrained contact cue; checker shadows are intentionally absent.
    g.roundRect(1.5, 2, size, size, radius).fill({ color: 0x000000, alpha: 0.16 });
    // base face (lower tone) + gold edge
    g.roundRect(0, 0, size, size, radius).fill({ color: faceBot }).stroke({ width: 1.5, color: COLORS.gold, alpha: used ? 0.5 : 1 });
    // upper portion in the lighter tone → fakes a top-lit gradient
    g.roundRect(size * 0.05, size * 0.05, size * 0.9, size * 0.6, radius * 0.85).fill({ color: faceTop });
    // glossy top sheen
    g.roundRect(size * 0.1, size * 0.09, size * 0.8, size * 0.32, radius * 0.6).fill({ color: 0xffffff, alpha: used ? 0.08 : 0.32 });
    c.addChild(g);
    c.alpha = used ? 0.7 : 1;
    const u = size / 4;
    const pip = (px: number, py: number): void => {
      // tiny lighter halo below the pip for a subtle embossed/inset look, then the pip itself
      g.circle(px, py + size * 0.014, size * 0.092).fill({ color: 0xffffff, alpha: used ? 0.04 : 0.32 });
      g.circle(px, py, size * 0.092).fill({ color: pipColor });
    };
    const L = u;
    const M = size / 2;
    const R = size - u;
    const layout: Record<number, Array<[number, number]>> = {
      1: [[M, M]],
      2: [[L, L], [R, R]],
      3: [[L, L], [M, M], [R, R]],
      4: [[L, L], [R, L], [L, R], [R, R]],
      5: [[L, L], [R, L], [M, M], [L, R], [R, R]],
      6: [[L, L], [R, L], [L, M], [R, M], [L, R], [R, R]],
    };
    for (const [px, py] of layout[value] ?? []) pip(px, py);
    return c;
  }

  /** Briefly tumbles a die from the board center into its server-determined resting slot. */
  private tumbleDie(
    node: Container,
    finalX: number,
    finalY: number,
    size: number,
    index: number,
    originX: number,
  ): void {
    if (prefersReducedMotion()) return;

    const direction = index % 2 === 0 ? -1 : 1;
    const startX = originX + direction * size * (0.55 + index * 0.06);
    const startY = this.ly.spineY + ((index % 3) - 1) * size * 0.18;
    const startRotation = direction * (Math.PI * 2.2 + index * 0.35);
    const finalAlpha = node.alpha;
    node.position.set(startX, startY);
    node.rotation = startRotation;
    node.scale.set(0.72);
    node.alpha = 0;

    animate({
      duration: 420 + index * 35,
      delay: index * 55,
      bind: node,
      onUpdate: (t) => {
        node.alpha = finalAlpha;
        node.position.set(
          startX + (finalX - startX) * t,
          startY + (finalY - startY) * t - Math.sin(Math.PI * t) * size * 0.28,
        );
        node.rotation = startRotation * (1 - t);
        node.scale.set(0.72 + 0.28 * t + Math.sin(Math.PI * t) * 0.08);
      },
      onComplete: () => {
        if (node.destroyed) return;
        node.position.set(finalX, finalY);
        node.rotation = 0;
        node.scale.set(1);
        node.alpha = finalAlpha;
      },
    });
  }

  // ---- pods / timer / dialogs (shared chrome, mirrors Hokm) ----

  private playerPod(
    side: 'me' | 'opp',
    d: { avatar: number; name: string; timeBank: number; roundWins: number; totalRounds: number; color: BeadColor; active: boolean },
  ): Container {
    const podW = this.podW;
    const pod = new Panel(podW, POD_H, { fill: COLORS.panel, alpha: 0.92, shadow: true });
    pod.position.set(PAD, side === 'me' ? this.mePodY : this.oppPodY);

    // Vertical pod: avatar centered at the top, then name / score / colour chip / time chip beneath it.
    const av = avatarSprite(this.ctx.profileAtlas, d.avatar, POD_AVATAR);
    av.position.set((podW - POD_AVATAR) / 2, POD_AV_TOP);
    pod.addChild(av);

    const roundStr = d.totalRounds > 0 ? `${toPersianDigits(d.roundWins)}/${toPersianDigits(d.totalRounds)}` : `${toPersianDigits(d.roundWins)}`;
    const scoreStr = `امتیاز ${roundStr}`;

    const cx = podW / 2;
    const nameY = POD_AV_TOP + POD_AVATAR + 12;
    pod.addChild(this.centerLabel(d.name, cx, nameY, 19, COLORS.text, '700', podW - 12));
    pod.addChild(this.centerLabel(scoreStr, cx, nameY + 26, 15, COLORS.gold, '700', podW - 12));
    if (d.color !== BeadColor.NotSet) pod.addChild(this.colorChip(d.color, cx, nameY + 49));
    pod.addChild(this.timeChip(d.timeBank, cx, nameY + 76, 'center'));

    // Whose-turn-it-is: a soft gold halo + crisp gold edge around the active player's pod.
    if (d.active) {
      const glow = new Graphics()
        .roundRect(0, 0, podW, POD_H, 15)
        .stroke({ width: 5, color: COLORS.gold, alpha: 0.22 })
        .roundRect(1.5, 1.5, podW - 3, POD_H - 3, 14)
        .stroke({ width: 2, color: COLORS.gold, alpha: 0.95 });
      pod.addChild(glow);
    }
    return pod;
  }

  /** A small centered "● سفید / سیاه" chip telling the player which checker colour they are. */
  private colorChip(color: BeadColor, cx: number, y: number): Container {
    const c = new Container();
    const white = color === BeadColor.White;
    const r = 7;
    const h = 22;
    const gap = 6;
    const label = makeText(white ? 'سفید' : 'سیاه', { size: 14, weight: '700', fill: COLORS.text, align: 'right' });
    label.anchor.set(0, 0.5);
    const totalW = r * 2 + gap + Math.ceil(label.width);
    const dot = new Graphics()
      .circle(r, h / 2, r)
      .fill({ color: white ? 0xf3e9d2 : 0x2a1c0e })
      .stroke({ width: 1.5, color: COLORS.gold, alpha: 0.9 });
    label.position.set(r * 2 + gap, h / 2);
    c.addChild(dot, label);
    c.position.set(cx - totalW / 2, y);
    return c;
  }

  /** A horizontally-centered, width-clamped label for the narrow vertical pods. */
  private centerLabel(text: string, cx: number, y: number, size: number, fill: number, weight: '400' | '700', maxW: number): Text {
    const t = makeText(text, { size, weight, fill, align: 'center' });
    t.anchor.set(0.5, 0);
    t.position.set(cx, y);
    if (t.width > maxW) t.scale.set(maxW / t.width);
    return t;
  }

  private timeChip(seconds: number, anchorX: number, y: number, side: 'me' | 'opp' | 'center'): Container {
    const c = new Container();
    const padX = 10;
    const h = 30;
    const iconSize = 22;
    const icon = iconSprite(this.ctx.iconTexture('clock'), iconSize);
    const num = makeText(`${toPersianDigits(seconds)}″`, { size: 19, weight: '700', fill: COLORS.text, align: 'left' });
    num.anchor.set(0, 0.5);
    const chipW = padX + iconSize + 6 + Math.ceil(num.width) + padX;
    const bg = new Graphics()
      .roundRect(0, 0, chipW, h, h / 2)
      .fill({ color: COLORS.darkPill, alpha: 0.85 })
      .stroke({ width: BORDER.thin, color: COLORS.panelBorder, alpha: 0.6 });
    icon.position.set(padX, (h - iconSize) / 2);
    num.position.set(padX + iconSize + 6, h / 2);
    c.addChild(bg, icon, num);
    const x = side === 'center' ? anchorX - chipW / 2 : side === 'me' ? anchorX : anchorX - chipW;
    c.position.set(x, y);
    return c;
  }

  private turnTimer(cx: number, cy: number, turnSeconds: number, reserveSeconds: number): TweenHandle | null {
    if (turnSeconds <= 0 && reserveSeconds <= 0) return null;
    const nr = 19;
    const node = new Container();
    const bg = new Graphics();
    const t = makeText('', { size: 20, weight: '700', align: 'center', fill: COLORS.white });
    t.anchor.set(0.5);
    node.addChild(bg, t);
    node.position.set(cx, cy + TURN_RING_R);
    this.board.addChild(node);

    const paintNode = (color: number): void => {
      bg.clear();
      bg.circle(0, 0, nr + 1).fill({ color: COLORS.scrim, alpha: 0.5 }).circle(0, 0, nr).fill({ color: COLORS.darkPill, alpha: 1 }).stroke({ width: BORDER.thick, color });
    };

    let active: TweenHandle | null = null;
    let lastSec = -1;

    const startReserve = (): void => {
      paintNode(COLORS.danger);
      t.style.fill = COLORS.danger;
      lastSec = -1;
      pulse(node, 1.3, 320);
      active = turnRing(this.board, {
        cx,
        cy,
        radius: TURN_RING_R,
        seconds: reserveSeconds,
        color: COLORS.danger,
        width: 7,
        onTick: (s) => {
          if (t.destroyed) return;
          t.text = toPersianDigits(s);
          if (s !== lastSec) {
            lastSec = s;
            pulse(node, 1.14, 280);
          }
        },
      });
      this.board.addChild(node);
    };

    if (turnSeconds > 0) {
      paintNode(WIN_GREEN);
      active = turnRing(this.board, {
        cx,
        cy,
        radius: TURN_RING_R,
        seconds: turnSeconds,
        color: WIN_GREEN,
        width: 7,
        onTick: (s) => {
          if (!t.destroyed) t.text = toPersianDigits(s);
        },
        onComplete: () => {
          active?.cancel();
          active = null;
          if (reserveSeconds > 0) startReserve();
        },
      });
      this.board.addChild(node);
    } else {
      startReserve();
    }

    return {
      cancel() {
        active?.cancel();
        if (!node.destroyed) node.destroy();
      },
    };
  }

  // ---- menus / dialogs ----

  /** Spectators leave the watch list; seated players open the regular game menu. */
  private menuAction(): void {
    if (!this.isPreview) {
      this.openMenu();
      return;
    }
    this.ctx.client.send(encodeExitView());
    this.ctx.navigate(RoomName.MainMenu);
  }

  private openMenu(): void {
    this.clearOverlay();
    const layer = new Container();
    const dim = new Graphics().rect(0, 0, viewport.w, viewport.h).fill({ color: COLORS.scrim, alpha: 0.55 });
    dim.eventMode = 'static';
    dim.on('pointertap', () => this.clearOverlay());
    layer.addChild(dim);

    const boxW = 520;
    const boxH = 330;
    const panel = new Panel(boxW, boxH);
    panel.position.set((viewport.w - boxW) / 2, (viewport.h - boxH) / 2);
    layer.addChild(panel);

    const title = makeText('منوی بازی', { size: 30, weight: '700', align: 'center', fill: COLORS.gold });
    title.anchor.set(0.5, 0);
    title.position.set(boxW / 2, 26);
    panel.addChild(title);

    const resign = new Button('اعلام باخت', () => this.confirmResign(), { width: boxW - 80, height: 64, fill: COLORS.danger });
    resign.position.set(40, 84);
    panel.addChild(resign);

    const halfW = (boxW - 80 - 20) / 2;
    const chatToggle = new Button(
      this.chatEnabled ? 'خاموش کردن چت' : 'روشن کردن چت',
      () => {
        this.chatEnabled = !this.chatEnabled;
        this.clearOverlay();
      },
      { width: halfW, height: 64, fontSize: 20, fill: COLORS.panelSoft, textColor: COLORS.text },
    );
    chatToggle.position.set(40, 164);
    panel.addChild(chatToggle);

    const soundToggle = new Button(
      isSfxMuted() ? 'روشن کردن صدا' : 'خاموش کردن صدا',
      () => {
        setSfxMuted(!isSfxMuted());
        this.clearOverlay();
      },
      { width: halfW, height: 64, fontSize: 20, fill: COLORS.panelSoft, textColor: COLORS.text },
    );
    soundToggle.position.set(40 + halfW + 20, 164);
    panel.addChild(soundToggle);

    const close = new Button('بستن', () => this.clearOverlay(), { width: boxW - 80, height: 64 });
    close.position.set(40, 244);
    panel.addChild(close);

    this.overlay.addChild(layer);
    scaleIn(panel, 220, 0.85);
  }

  private confirmResign(): void {
    this.confirmDialog('اعلام باخت', 'با اعلام باخت، این دست را واگذار می‌کنید و حریف برنده می‌شود.', 'بله، باخت را می‌پذیرم', () => this.resignGame());
  }

  private confirmDialog(titleText: string, message: string, confirmLabel: string, onConfirm: () => void): void {
    this.clearOverlay();
    const layer = new Container();
    const dim = new Graphics().rect(0, 0, viewport.w, viewport.h).fill({ color: COLORS.scrim, alpha: SCRIM.dialog });
    dim.eventMode = 'static';
    dim.on('pointertap', () => this.openMenu());
    layer.addChild(dim);

    const boxW = 560;
    const boxH = 320;
    const panel = new Panel(boxW, boxH);
    panel.position.set((viewport.w - boxW) / 2, (viewport.h - boxH) / 2);
    panel.eventMode = 'static';
    panel.hitArea = new Rectangle(0, 0, boxW, boxH);
    layer.addChild(panel);

    const title = makeText(titleText, { size: 30, weight: '700', align: 'center', fill: COLORS.danger });
    title.anchor.set(0.5, 0);
    title.position.set(boxW / 2, 30);
    panel.addChild(title);

    const msg = makeText(message, { size: 22, align: 'center', fill: COLORS.text });
    msg.anchor.set(0.5, 0);
    msg.style.wordWrap = true;
    msg.style.wordWrapWidth = boxW - 80;
    msg.position.set(boxW / 2, 102);
    panel.addChild(msg);

    const btnW = (boxW - 100) / 2;
    const yes = new Button(confirmLabel, onConfirm, { width: btnW, height: 64, fontSize: 20, fill: COLORS.danger });
    yes.position.set(40, boxH - 92);
    panel.addChild(yes);
    const no = new Button('بازگشت', () => this.openMenu(), { width: btnW, height: 64, fill: COLORS.panelSoft, textColor: COLORS.text });
    no.position.set(40 + btnW + 20, boxH - 92);
    panel.addChild(no);

    this.overlay.addChild(layer);
    scaleIn(panel, 220, 0.85);
  }

  private showEndGame(resultText: string, reasonText: string): void {
    this.clearOverlay();
    const layer = new Container();
    const dim = new Graphics().rect(0, 0, viewport.w, viewport.h).fill({ color: COLORS.scrim, alpha: 0.7 });
    dim.eventMode = 'static';
    layer.addChild(dim);

    const boxW = 600;
    const boxH = 420;
    const panel = new Panel(boxW, boxH);
    panel.position.set((viewport.w - boxW) / 2, (viewport.h - boxH) / 2);
    layer.addChild(panel);

    const title = makeText('پایان بازی', { size: 32, weight: '700', align: 'center', fill: COLORS.gold });
    title.anchor.set(0.5, 0);
    title.position.set(boxW / 2, 28);
    panel.addChild(title);

    const result = makeText(resultText, { size: 26, weight: '700', align: 'center', fill: COLORS.white });
    result.style.wordWrap = true;
    result.style.wordWrapWidth = boxW - 64;
    result.anchor.set(0.5, 0);
    result.position.set(boxW / 2, 110);
    panel.addChild(result);

    if (reasonText) {
      const reason = makeText(reasonText, { size: 22, align: 'center', fill: COLORS.textDim });
      reason.style.wordWrap = true;
      reason.style.wordWrapWidth = boxW - 64;
      reason.anchor.set(0.5, 0);
      reason.position.set(boxW / 2, 200);
      panel.addChild(reason);
    }

    const back = new Button('بازگشت به منو اصلی', () => this.ctx.navigate(RoomName.MainMenu), { width: boxW - 80, height: 72, fontSize: 26 });
    back.position.set(40, boxH - 96);
    panel.addChild(back);

    this.overlay.addChild(layer);
    scaleIn(panel, 260, 0.8);
    if (/برد|برنده|مارس کرد/.test(resultText)) confettiBurst(this.fx);
  }

  private resignGame(): void {
    if (!this.resignFlow.begin()) return;

    this.selectedRow = null;
    this.highlights = [];
    this.clearOverlay();
    this.banner('در انتظار پاسخ سرور…', COLORS.gold, RESIGN_RESPONSE_TIMEOUT_MS);
    this.render();
    try {
      this.ctx.client.send(encodeResignGame());
    } catch {
      this.resignFlow.acknowledge();
      this.clearOverlay();
      this.banner('درخواست ارسال نشد؛ اتصال به سرور در دسترس نیست.', COLORS.danger);
      return;
    }

    this.after(RESIGN_RESPONSE_TIMEOUT_MS, () => {
      if (!this.resignFlow.acknowledge()) return;
      this.clearOverlay();
      this.banner('پاسخی از سرور دریافت نشد؛ دوباره تلاش کنید.', COLORS.danger);
    });
  }

  private consumeResignFeedback(): boolean {
    const preserve = this.preserveResignFeedback;
    this.preserveResignFeedback = false;
    return preserve;
  }

  // ---- small helpers ----

  private centered(text: string, y: number, opts: { size?: number; weight?: '400' | '700'; fill?: number } = {}): Container {
    const t = makeText(text, { size: opts.size ?? 24, weight: opts.weight ?? '400', align: 'center', fill: opts.fill ?? COLORS.text });
    t.anchor.set(0.5);
    t.position.set(viewport.w / 2, y);
    const c = new Container();
    c.addChild(t);
    return c;
  }

  private banner(text: string, color: number, durationMs = 3000): void {
    const c = new Container();
    const w = Math.min(viewport.w - 120, 720);
    const h = 60;
    const panel = new Panel(w, h);
    panel.position.set((viewport.w - w) / 2, Math.round(viewport.h * 0.2));
    const t = makeText(text, { size: 22, align: 'center', fill: color });
    t.anchor.set(0.5);
    t.style.wordWrap = true;
    t.style.wordWrapWidth = w - 40;
    t.position.set(w / 2, h / 2);
    panel.addChild(t);
    c.addChild(panel);
    this.overlay.addChild(c);
    scaleIn(panel, 200, 0.85);
    this.after(durationMs, () => {
      if (!c.destroyed) c.destroy({ children: true });
    });
  }

  private clearOverlay(): void {
    for (const child of this.overlay.removeChildren()) child.destroy({ children: true });
  }

  private after(ms: number, fn: () => void): void {
    const id = window.setTimeout(fn, ms);
    this.track(() => window.clearTimeout(id));
  }
}
